import {
  UndefinedInitialDataOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { clientServices } from "./client.services";
import { PaginationResponse, type QueryParams as TQueryParams } from "../type";
import { CLIENT_KEYS } from "./client.keys";
import { TClient } from "~/types";
import { QueryClientParams } from "./client.types";
import { baseService } from "../base";

export const useGetClients = (opts?: {
  params?: QueryClientParams & TQueryParams;
  query?: Omit<
    UndefinedInitialDataOptions<PaginationResponse<TClient>>,
    "queryKey" | "queryFn"
  >;
}) => {
  return useQuery({
    queryKey: CLIENT_KEYS.GET_CLIENTS(opts?.params),
    queryFn: () => clientServices.getClients(opts?.params),
    ...opts?.query,
  });
};

export const useGetClient = (id?: string) => {
  return useQuery({
    queryKey: CLIENT_KEYS.GET_CLIENT(id!),
    queryFn: () => clientServices.getClient(id!),
    enabled: !!id,
  });
};

export const useGetClientsInfinite = (
  params?: QueryClientParams & TQueryParams
) => {
  return useInfiniteQuery({
    queryKey: CLIENT_KEYS.GET_INFINITE_CLIENTS(params),
    queryFn: ({ pageParam }) =>
      clientServices.getClients({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const { currentPage, totalPages } = lastPage.meta;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetSubClients = (
  clientId: string,
  options?: {
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ["sub-clients", clientId],
    queryFn: () => clientServices.getSubClient(clientId),
    enabled: options?.enabled !== undefined ? options.enabled : !!clientId,
  });
};

// Interface for clients with storage properties
export interface ClientWithStorage {
  id: string;
  companyName: string;
  storageRate: number;
  includeInsurance: boolean;
  insuranceRate?: number;
  lastInvoiceDate?: string;
  quickBooksId: string;
}

// Hook to get clients with active storage (real API call)
export const useGetClientsWithActiveStorage = () => {
  return useQuery({
    queryKey: ["clients-with-active-storage"],
    queryFn: async () => {
      try {
        // Use direct API call for storage clients
        const response = await baseService.get("/api/storage/clients");
        
        const clientsWithStorage: ClientWithStorage[] = response.data;
        return clientsWithStorage;
      } catch (error) {
        console.error("Error loading clients with storage:", error);
        // Return empty array with message if API fails
        return [];
      }
    },
  });
};
